import { iconExternalLink, iconShop } from '@hover/icons';
import { assertUnreachable } from '@hover/utilities';
import type { PublicOrgInfo } from 'mock/generated/types';

import type { Org } from 'src/shared/graphql/generated';
import { getApiEnvironment } from 'src/shared/utils/environment';

import type { PartnerMetadata } from '../constants/partners';
import {
  DESIGNER_PARTNER_MAP,
  DesignerPartner,
  MaterialCalloutType,
  NO_PARTNER_METADATA,
  PARTNER_CONFIGURATION_MAP,
} from '../constants/partners';
import type { Brand, Project } from '../graphql/generated';

import { warn } from './logging';

export const getPartnerConfiguration = (orgId: number | undefined) => {
  const environment = getApiEnvironment();

  if (!environment) {
    warn(
      'getPartnerConfiguration: unable to determine API environment required to retrieve partner configuration',
    );
    return null;
  }

  const map = DESIGNER_PARTNER_MAP[environment];

  if (!map) {
    warn(
      `getPartnerConfiguration: no partner configuration found for environment "${environment}"`,
    );
    return null;
  }

  if (!orgId) return null;

  return map[orgId.toString()];
};

export const publicOrgInfoToPartnerMetadata = (
  publicOrgInfo:
    | Pick<
        PublicOrgInfo,
        'id' | 'brandLogoUrl' | 'displayName' | 'brandLogoUrlInvert'
      >
    | undefined
    | null,
): PartnerMetadata => {
  if (!publicOrgInfo) return NO_PARTNER_METADATA;

  const partner =
    getPartnerConfiguration(publicOrgInfo?.id) || DesignerPartner.NONE;

  return {
    partner,
    orgId: publicOrgInfo.id,
    displayName: publicOrgInfo.displayName,
    logoUrl: publicOrgInfo.brandLogoUrl ?? undefined,
    logoUrlInvert: publicOrgInfo.brandLogoUrlInvert ?? undefined,
    configuration: PARTNER_CONFIGURATION_MAP[partner],
  };
};

export type OrgWithPartnerInfo = Pick<Org, 'id' | 'customerDisplayName'> & {
  brand?: Pick<Brand, 'logoUrl'> | null;
};

export const orgToPartnerMetadata = (
  org: OrgWithPartnerInfo | undefined | null,
): PartnerMetadata => {
  const partner = getPartnerConfiguration(org?.id);

  if (!org || !partner) return NO_PARTNER_METADATA;

  return {
    partner,
    orgId: org.id,
    displayName: org.customerDisplayName ?? undefined,
    logoUrl: org.brand?.logoUrl ?? undefined,
    // Note: This explicit undefined means that Pros won't see a logo on dark backgrounds,
    // but that's ok because we're removing this Pro fallback logic with MEOW-5904
    logoUrlInvert: undefined,
    configuration: PARTNER_CONFIGURATION_MAP[partner],
  };
};

export type ProjectWithPartnerInfo = Pick<Project, 'publicOrgInfo'>;

export interface AiOriginalImageWithPartnerInfo {
  associatedProject?: ProjectWithPartnerInfo | null | undefined;
}

export const getMaterialCalloutDisplay = (type: MaterialCalloutType) => {
  switch (type) {
    case MaterialCalloutType.SHOP:
      return {
        icon: iconShop,
        i18nKey: 'materialCallouts.calloutButton.shop' as const,
      };
    case MaterialCalloutType.APPOINTMENT:
      return {
        icon: iconExternalLink,
        i18nKey: 'materialCallouts.calloutButton.appointment' as const,
      };
    default:
      return assertUnreachable(type);
  }
};

export type MaterialCalloutDisplay = ReturnType<
  typeof getMaterialCalloutDisplay
>;
